import { isProfileEditorPath } from "../constants/listings";
import { findMemberAccess, resolveTeamPermissions } from "./teamPermissions";

const LISTING_DETAIL_PATH = /\/my-(events|jobs|organizations)\/[^/]+/;

const resolveListingRouteContext = (pathname, search) => {
  const resolvedPathname = pathname ?? (typeof window !== "undefined" ? window.location.pathname : "");
  const resolvedSearch = search ?? (typeof window !== "undefined" ? window.location.search : "");

  return { pathname: resolvedPathname, search: resolvedSearch };
};

/**
 * Resolves api-v2 vs api-v2-team for listing editor/list/detail calls.
 *
 * - Personal API: logged-in member loading their own listing (no ?user_id=).
 * - Team API: /profile team editor, explicit ?user_id=, or team member editing owner listings.
 */
export const resolveListingTeamContext = (getState, pathname, search) => {
  const route = resolveListingRouteContext(pathname, search);
  const urlOwnerId = new URLSearchParams(route.search || "").get("user_id");
  const normalizedPath = String(route.pathname || "");
  const isListingDetailPath = LISTING_DETAIL_PATH.test(normalizedPath);

  const {
    teams: { currentTeam },
    account: { profile }
  } = getState();

  const profileUserId = profile?.user_id != null ? String(profile.user_id) : "";
  const currentTeamOwnerId = currentTeam?.value ? String(currentTeam.value) : "";
  const isTeamMemberContext = Boolean(currentTeamOwnerId && profileUserId) && currentTeamOwnerId !== profileUserId;

  if (urlOwnerId) {
    return {
      apiPrefix: "/members/api-v2-team",
      userIdParams: { user_id: String(urlOwnerId) }
    };
  }

  if (isProfileEditorPath(normalizedPath) && currentTeamOwnerId) {
    return {
      apiPrefix: "/members/api-v2-team",
      userIdParams: { user_id: currentTeamOwnerId }
    };
  }

  if (isListingDetailPath && isTeamMemberContext && currentTeamOwnerId) {
    return {
      apiPrefix: "/members/api-v2-team",
      userIdParams: { user_id: currentTeamOwnerId }
    };
  }

  return { apiPrefix: "/members/api-v2", userIdParams: {} };
};

export const buildListingTeamSearch = (getState, pathname, search) => {
  const { userIdParams } = resolveListingTeamContext(getState, pathname, search);
  return userIdParams.user_id ? `?user_id=${userIdParams.user_id}` : "";
};

/**
 * Owner id of the team account the member is buying for, or "" when they buy for their own account (GROWTH-7126).
 *
 * Used by the Upgrade / Renew / Pay-pending requests, which every team Admin must send for the owner's listing. The
 * source is the one the card requests (actions/payment.js) and publishListing already use, `teams.currentTeam.value`,
 * except that the member's own id counts as personal, as resolveTeamPermissions treats it. Unlike
 * resolveListingTeamContext this does not depend on the route, so the My Listings page and the detail page agree.
 *
 * `getState` may be missing: the fetch thunks are also called with `dispatch` only, and that means personal.
 */
export const resolveFinancialTeamOwnerId = getState => {
  if (typeof getState !== "function") {
    return "";
  }

  const state = getState();
  const currentTeamOwnerId = state?.teams?.currentTeam?.value ? String(state.teams.currentTeam.value) : "";
  const profileUserId = state?.account?.profile?.user_id != null ? String(state.account.profile.user_id) : "";

  return currentTeamOwnerId && currentTeamOwnerId !== profileUserId ? currentTeamOwnerId : "";
};

/**
 * Whether the signed-in member may write to the listings of the account a listing request addresses (GROWTH-7138).
 *
 * api-v2-team refuses every POST, PUT, PATCH and DELETE below the Content Manager role, so a write sent for a View-Only
 * member always fails. This answers false only when that role is known; the member's own account, and a team whose
 * member list has not delivered the role, answer true, so the caller keeps sending the request it sends today and the
 * server stays the gate. "May edit" is resolveTeamPermissions().canEditListings, the rule useTeamPermissions applies.
 *
 * The owner is the one resolveListingTeamContext would address, so the answer follows the request, not the dropdown.
 * `getState` may be missing, as for resolveFinancialTeamOwnerId; that means personal.
 */
export const canWriteListingsInTeamContext = (getState, pathname, search) => {
  if (typeof getState !== "function") {
    return true;
  }

  const ownerId = resolveListingTeamContext(getState, pathname, search).userIdParams.user_id;
  if (!ownerId) {
    return true;
  }

  const state = getState();
  const access = findMemberAccess(state?.teams?.member, ownerId, { activeOnly: true });
  const permissions = resolveTeamPermissions({
    profileUserId: state?.account?.profile?.user_id,
    ownerId,
    role: access?.role != null ? Number(access.role) : null
  });

  return permissions.isRolePending || permissions.canEditListings;
};
